/**
 * window.__agentGameApi — command channel between an AI agent (Playwright) and the
 * Unity game running in the PlayGameUnity iframe.
 *
 * Two layers:
 *  1. High-level typed macros — USE THESE: goTo(AgentGameKnownLocation), collectUridium,
 *     depositUridium, collectOxygen, runDebugAction(AgentGameDebugAction), waitForPhase…
 *     The complete vocabulary (where you can go, what you can collect, every debug action)
 *     is the enums in pure-js/types/AgentControl.ts; `describeKnownLocations()` lists it live.
 *  2. Low-level `sendCommand(action, params)` — raw bridge protocol, for anything else.
 *
 * Outbound:  unityInstance.SendMessage('AgentBridge', 'AgentCommand', json)
 * Inbound:   the game's AgentNavigate.jslib fires `onAgentCommandResult(json)` and
 *            `onAgentState(json)` on the IFRAME window; handlers here correlate results
 *            back to pending promises by command id.
 */
import {
  AGENT_KNOWN_LOCATIONS,
  AgentGameKnownLocation,
  AgentGameAbility,
  AgentGameDebugAction,
  AgentGameDebugTarget,
  AgentGameDronePuzzleInteraction,
  AgentGameItem,
  AgentGameLocationKind,
  AgentGamePath,
  AgentGamePhase,
  AgentGameReflectionPhase,
  AgentGameRobotState,
  type AgentGameAction,
  type AgentGameRobotInfo,
  type AgentGameCommand,
  type AgentGameCommandResult,
  type AgentGameLocation,
  type AgentGameStateSnapshot,
  type AgentKnownLocationMatcher
} from '../../../pure-js/types/AgentControl'
import { delay } from '../../../pure-js/libs/Common'
import type { UnityContentWindow } from '../features/gamer/helpers/useLifeCycleMethods'

const DEFAULT_COMMAND_TIMEOUT_MS = 120_000
const AGENT_BRIDGE_GAMEOBJECT = 'AgentBridge'
const AGENT_BRIDGE_METHOD = 'AgentCommand'

type AgentGameWindow = UnityContentWindow & {
  onAgentCommandResult?: (json: string) => void
  onAgentState?: (json: string) => void
}

export type AgentLocationFilter = {
  kind?: string
  item?: AgentGameItem | string
  path?: AgentGamePath | string
  nameIncludes?: string
}

export type AgentGameApi = {
  ready: boolean

  // --- high-level, typed (preferred) ---
  /** Liveness check against the in-game bridge. */
  ping: () => Promise<AgentGameCommandResult>
  /** All interactables currently in the scene, optionally filtered. */
  listLocations: (filter?: AgentLocationFilter) => Promise<AgentGameLocation[]>
  /** Every AgentGameKnownLocation key with a human description — "where can I go?". */
  describeKnownLocations: () => Record<string, string>
  /** Walk to a known place (enum) or a concrete location id/name. */
  goTo: (location: AgentGameKnownLocation | string) => Promise<AgentGameCommandResult>
  /** Walk to + pick up the nearest item of this type (optionally on a specific path). */
  collect: (item: AgentGameItem, path?: AgentGamePath) => Promise<AgentGameCommandResult>
  collectUridium: (path?: AgentGamePath) => Promise<AgentGameCommandResult>
  collectOxygen: () => Promise<AgentGameCommandResult>
  collectHeater: () => Promise<AgentGameCommandResult>
  collectStimpack: () => Promise<AgentGameCommandResult>
  /** Walk to the base deposit and drop carried uridium. */
  depositUridium: () => Promise<AgentGameCommandResult>
  /** Walk into the elevator's zone (it is not an interactable); be there when the loop ends. */
  goToElevator: () => Promise<AgentGameCommandResult>
  /** Host only, dev builds: end the loop once everyone is on board (`elevator.canLeave`). */
  leaveThroughElevator: () => Promise<AgentGameCommandResult>
  openNearestDoor: () => Promise<AgentGameCommandResult>
  /**
   * Walk to a raw world position on the ground. Target a WALKABLE (ground-level) point —
   * arbitrary offsets or elevated positions are off the NavMesh and get rejected.
   */
  moveTo: (x: number, z: number, y?: number) => Promise<AgentGameCommandResult>
  /** Walk to + interact with a concrete interactable (id from listLocations, or name). */
  interactWith: (locationIdOrName: string) => Promise<AgentGameCommandResult>
  /** Fire a debug/test-setup action (SkipOnboarding, AddUridium, EndSession, …). */
  runDebugAction: (action: AgentGameDebugAction, target?: AgentGameDebugTarget) => Promise<AgentGameCommandResult>
  /** Resolve once the game reaches the phase (polls state snapshots). */
  waitForPhase: (phase: AgentGamePhase, timeoutMs?: number) => Promise<AgentGameStateSnapshot>
  /** Resolve once reflection reaches the given sub-phase (see getState().reflection). */
  waitForReflectionPhase: (phase: AgentGameReflectionPhase, timeoutMs?: number) => Promise<AgentGameStateSnapshot>
  stop: () => Promise<AgentGameCommandResult>

  // --- reflection phase ---
  /** Mark this player ready on the current group question / phase. */
  reflectionReady: () => Promise<AgentGameCommandResult>
  /** Answer the current individual scale question (0-based index). Auto-advances. */
  answerReflectionQuestion: (index: number) => Promise<AgentGameCommandResult>
  /** Pick an emotion on the circumplex, x/y in -2..2 (|x|+|y|=3 forbidden). */
  submitCircumplex: (x: number, y: number) => Promise<AgentGameCommandResult>
  /** Host only: start the next loop after post-reflection. */
  reflectionNextLoop: () => Promise<AgentGameCommandResult>
  /** Host only: enter the final group reflection. */
  reflectionFinal: () => Promise<AgentGameCommandResult>
  /** Host only: quit the game from final reflection (fires onGameEnded). */
  reflectionQuit: () => Promise<AgentGameCommandResult>

  // --- onboarding phase (spawn is OUTSIDE the base) ---
  /** Walk to the onboarding exit point; the game proceeds when ALL players are there. */
  goToOnboardingExit: () => Promise<AgentGameCommandResult>

  // --- robot unlock (two-player mechanic) ---
  /** Robot state + displayed code (field player) + map candidate codes. */
  getRobotInfo: (interactableIdOrName?: string) => Promise<AgentGameRobotInfo | null>
  /** Map player: press candidate code button N (index into candidateCodes). */
  pressRobotCode: (index: number) => Promise<AgentGameCommandResult>
  /** Map player: press the candidate button whose code matches (from the field player). */
  pressRobotCodeMatching: (code: string) => Promise<AgentGameCommandResult>
  /** Map player: click the awaiting robot's marker so its codes show (openMap first). */
  selectRobotOnMap: (robotId?: string) => Promise<AgentGameCommandResult>
  /** The nearest robot ready to be unlocked, optionally on one path. */
  nearestRobot: (path?: AgentGamePath) => Promise<AgentGameLocation | undefined>
  /** Field player: walk to the robot (doors open on the way) and start its unlock; `data.code` is what to read out. */
  startRobotUnlock: (robotId: string) => Promise<AgentGameCommandResult>
  /** Field player: once the map player pressed the code, take the uridium from the open robot. */
  takeUridiumFromRobot: (robotId: string) => Promise<AgentGameCommandResult>
  /** Seconds of oxygen carried in the backpack. */
  oxygenSecondsLeft: () => number
  /** Deposit any carried uridium at the base, then walk to the elevator. */
  goHome: () => Promise<AgentGameCommandResult>

  // --- preplanning phase ---
  /** Pick an ability (must be unique across ready players). */
  selectAbility: (ability: AgentGameAbility) => Promise<AgentGameCommandResult>
  togglePreplanReady: () => Promise<AgentGameCommandResult>

  // --- in-game HUD ---
  dropItem: (slot: number) => Promise<AgentGameCommandResult>
  useStimpack: () => Promise<AgentGameCommandResult>
  useHeater: () => Promise<AgentGameCommandResult>
  useAbility: () => Promise<AgentGameCommandResult>
  callDrone: () => Promise<AgentGameCommandResult>
  toggleCircumplexHud: () => Promise<AgentGameCommandResult>
  /** Aborts the game for this player (fires onGameAborted). */
  abortGame: () => Promise<AgentGameCommandResult>

  // --- drone puzzle / map / lobby / dialogs ---
  dronePuzzle: (interaction: AgentGameDronePuzzleInteraction) => Promise<AgentGameCommandResult>
  openMap: () => Promise<AgentGameCommandResult>
  closeMap: () => Promise<AgentGameCommandResult>
  /** Requires the map to be open (openMap first). */
  placeFlag: (x: number, z: number) => Promise<AgentGameCommandResult>
  /** Host only: start the game from the lobby. */
  startLobbyGame: () => Promise<AgentGameCommandResult>
  leaveLobby: () => Promise<AgentGameCommandResult>
  /** Master only: tick the lobby "everyone can hear each other" toggle (gates START). */
  confirmLobbyAudio: () => Promise<AgentGameCommandResult>
  /** Resolve an open confirm dialog (default: confirm). */
  resolveConfirmDialog: (confirm?: boolean) => Promise<AgentGameCommandResult>

  // --- low-level ---
  sendCommand: (
    action: AgentGameAction,
    params?: Record<string, unknown>,
    timeoutMs?: number
  ) => Promise<AgentGameCommandResult>
  getState: () => AgentGameStateSnapshot | null
  onState: (cb: (state: AgentGameStateSnapshot) => void) => () => void
}

type Pending = {
  resolve: (r: AgentGameCommandResult) => void
  timeout: ReturnType<typeof setTimeout>
}

const failed = (error: string): AgentGameCommandResult => ({ id: 'local', status: 'failed', error })

/** How far (world units) in FRONT of an off-navmesh interactable to stand before interacting. */
const APPROACH_DISTANCE = 2.5

const ROBOT_POLL_MS = 1_000 // 1 s
/** A robot shows its code at once; how long the map player gets to press it is the robot's own timer. */
const ROBOT_STATE_TIMEOUT_MS = 15_000 // 15 s
const ROBOT_OPEN_TIMEOUT_MS = 30_000 // 30 s
/** Inventory changes reach the state snapshot about once a second. */
const ITEM_SETTLE_MS = 2_000 // 2 s

/** `interactWith` to a wall/shelf interactable can fail to path to its exact (off-navmesh) transform. */
const isUnreachable = (result: AgentGameCommandResult): boolean =>
  result.status === 'failed' && /partial-navmesh-path|unreachable|no-navmesh/i.test(result.error ?? '')

/**
 * Container/shelf interactables carry NO `item` tag (item:null), so filtering pickups by item alone
 * misses them. Match those crates by name keyword so collectOxygen/collectHeater/collectStimpack find
 * the wall container (verified: onboarding + base `OxygenContainer`, base `HeaterContainer`), not only
 * loose `PickUpItem` drops.
 */
const ITEM_CONTAINER_KEYWORDS: Partial<Record<AgentGameItem, string>> = {
  [AgentGameItem.OXYGEN]: 'oxygen',
  [AgentGameItem.HEATER]: 'heater',
  [AgentGameItem.STIMPACK]: 'stimpack'
}

const matchesFilter = (location: AgentGameLocation, filter?: AgentLocationFilter): boolean => {
  if (!filter) return true
  if (filter.kind && location.kind !== filter.kind) return false
  if (filter.item && location.item !== filter.item) return false
  if (filter.path && location.path !== filter.path) return false
  if (filter.nameIncludes && !location.name.toLowerCase().includes(filter.nameIncludes.toLowerCase())) return false
  return true
}

const matchesKnown = (location: AgentGameLocation, matcher: AgentKnownLocationMatcher): boolean => {
  if (matcher.kind && location.kind !== matcher.kind) return false
  if (matcher.item && location.item !== matcher.item) return false
  if (matcher.path && location.path !== matcher.path) return false
  if (matcher.namePatterns?.length) {
    const name = location.name.toLowerCase()
    if (!matcher.namePatterns.some((p) => name.includes(p.toLowerCase()))) return false
  }
  return true
}

const nearest = (locations: AgentGameLocation[]): AgentGameLocation | undefined =>
  [...locations].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))[0]

/**
 * Attaches the agent game API to the given iframe. Called by PlayGameUnity once the
 * game is loaded (agent mode only). Returns a cleanup function.
 */
export const installAgentGameApi = (gameFrame: { current: HTMLIFrameElement | null }): (() => void) => {
  const pending = new Map<string, Pending>()
  const stateListeners = new Set<(state: AgentGameStateSnapshot) => void>()
  let lastState: AgentGameStateSnapshot | null = null
  let commandCounter = 0

  const getGameWindow = (): AgentGameWindow | undefined =>
    (gameFrame.current?.contentWindow as AgentGameWindow | null) ?? undefined

  const settle = (result: AgentGameCommandResult) => {
    const entry = pending.get(result.id)
    if (!entry) return
    clearTimeout(entry.timeout)
    pending.delete(result.id)
    entry.resolve(result)
  }

  const attachInboundHandlers = () => {
    const gameWindow = getGameWindow()
    if (!gameWindow) return false
    gameWindow.onAgentCommandResult = (json: string) => {
      try {
        settle(JSON.parse(json) as AgentGameCommandResult)
      } catch (e) {
        console.warn('[agentGameApi] unparseable command result', json, e)
      }
    }
    gameWindow.onAgentState = (json: string) => {
      try {
        lastState = JSON.parse(json) as AgentGameStateSnapshot
        stateListeners.forEach((cb) => cb(lastState as AgentGameStateSnapshot))
      } catch (e) {
        console.warn('[agentGameApi] unparseable state snapshot', json, e)
      }
    }
    return true
  }

  // Attach handlers now if the iframe window already exists; sendCommand re-attaches lazily
  // otherwise (the iframe window can appear after install).
  attachInboundHandlers()

  const sendCommand = (
    action: AgentGameAction,
    params?: Record<string, unknown>,
    timeoutMs = DEFAULT_COMMAND_TIMEOUT_MS
  ): Promise<AgentGameCommandResult> => {
    attachInboundHandlers()
    const gameWindow = getGameWindow()
    const instance = gameWindow?.unityInstance
    if (typeof instance?.SendMessage !== 'function') {
      return Promise.resolve(failed('Unity instance not ready'))
    }
    commandCounter += 1
    const command: AgentGameCommand = { id: `cmd-${commandCounter}-${Date.now()}`, action, params }
    return new Promise<AgentGameCommandResult>((resolve) => {
      const timeout = setTimeout(() => {
        pending.delete(command.id)
        resolve({ id: command.id, status: 'failed', error: `timeout after ${timeoutMs}ms` })
      }, timeoutMs)
      pending.set(command.id, { resolve, timeout })
      try {
        instance.SendMessage(AGENT_BRIDGE_GAMEOBJECT, AGENT_BRIDGE_METHOD, JSON.stringify(command))
      } catch (e) {
        clearTimeout(timeout)
        pending.delete(command.id)
        resolve({ id: command.id, status: 'failed', error: e instanceof Error ? e.message : String(e) })
      }
    })
  }

  const listLocations = async (filter?: AgentLocationFilter): Promise<AgentGameLocation[]> => {
    const result = await sendCommand('listLocations')
    if (result.status !== 'done') return []
    const locations = ((result.data as { locations?: AgentGameLocation[] })?.locations ?? []) as AgentGameLocation[]
    return locations.filter((l) => matchesFilter(l, filter))
  }

  /**
   * interactWith, but robust for off-navmesh wall/shelf interactables. Plain `interactWith` paths to
   * the target's exact transform; oxygen/heater CONTAINERS sit off the NavMesh, so from RANGE that
   * path fails (`target-unreachable-partial-navmesh-path`) even though the game interacts by proximity
   * once you're close. On that failure we walk to a WALKABLE floor point a couple of units in FRONT of
   * the target (toward the player's current position) and retry — automating the manual
   * moveTo-near-then-interact dance so collectOxygen()/interactWith() just work at range.
   */
  const interactWithApproach = async (
    interactableId: string,
    knownLocation?: AgentGameLocation
  ): Promise<AgentGameCommandResult> => {
    const first = await sendCommand('interactWith', { interactableId })
    if (!isUnreachable(first)) return first
    const target =
      knownLocation ?? (await listLocations()).find((l) => l.id === interactableId || l.name === interactableId)
    const player = lastState
    if (!target || !player) return first
    const dx = player.playerX - target.x
    const dz = player.playerZ - target.z
    const dist = Math.hypot(dx, dz)
    if (!dist) return first
    const approachX = target.x + (dx / dist) * APPROACH_DISTANCE
    const approachZ = target.z + (dz / dist) * APPROACH_DISTANCE
    await sendCommand('moveTo', { x: approachX, z: approachZ })
    return sendCommand('interactWith', { interactableId })
  }

  const resolveKnown = async (
    known: AgentGameKnownLocation
  ): Promise<{ location?: AgentGameLocation; error?: string }> => {
    const matcher = AGENT_KNOWN_LOCATIONS[known]
    if (!matcher) return { error: `unknown location key '${known}' — see describeKnownLocations()` }
    const all = await listLocations()
    const match = nearest(all.filter((l) => matchesKnown(l, matcher)))
    if (match) return { location: match }
    const seen = [...new Set(all.map((l) => l.name))].slice(0, 25).join(', ')
    return {
      error: `no match for '${known}' (${matcher.description}). Interactables currently in scene: ${seen || 'none'}`
    }
  }

  const goTo = async (location: AgentGameKnownLocation | string): Promise<AgentGameCommandResult> => {
    if ((Object.values(AgentGameKnownLocation) as string[]).includes(location)) {
      const { location: resolved, error } = await resolveKnown(location as AgentGameKnownLocation)
      if (!resolved) return failed(error as string)
      return sendCommand('goToNamed', { locationId: resolved.id })
    }
    return sendCommand('goToNamed', { locationId: location })
  }

  const collect = async (item: AgentGameItem, path?: AgentGamePath): Promise<AgentGameCommandResult> => {
    const all = await listLocations()
    const keyword = ITEM_CONTAINER_KEYWORDS[item]
    const candidates = all.filter((l) => {
      if (path && l.path !== path) return false
      if (l.item === item) return true
      return Boolean(keyword && l.name.toLowerCase().includes(keyword))
    })
    const target = nearest(candidates)
    if (!target) return failed(`no ${item} pickup or container found${path ? ` on path ${path}` : ''}`)
    return interactWithApproach(target.id, target)
  }

  const interactWithKnown = async (known: AgentGameKnownLocation): Promise<AgentGameCommandResult> => {
    const { location, error } = await resolveKnown(known)
    if (!location) return failed(error as string)
    return interactWithApproach(location.id, location)
  }

  const waitForSnapshot = (
    label: string,
    predicate: (state: AgentGameStateSnapshot) => boolean,
    timeoutMs: number
  ): Promise<AgentGameStateSnapshot> =>
    new Promise((resolve, reject) => {
      if (lastState && predicate(lastState)) {
        resolve(lastState)
        return
      }
      const timeout = setTimeout(() => {
        unsubscribe()
        reject(
          new Error(
            `${label} timed out after ${timeoutMs}ms (current phase: ${lastState?.phase}, reflection: ${lastState?.reflection?.phase})`
          )
        )
      }, timeoutMs)
      const unsubscribe = api.onState((state) => {
        if (!predicate(state)) return
        clearTimeout(timeout)
        unsubscribe()
        resolve(state)
      })
    })

  const waitForPhase = (phase: AgentGamePhase, timeoutMs = 10 * 60_000): Promise<AgentGameStateSnapshot> =>
    waitForSnapshot(`waitForPhase(${phase})`, (s) => s.phase === phase, timeoutMs)

  const waitForReflectionPhase = (
    phase: AgentGameReflectionPhase,
    timeoutMs = 10 * 60_000
  ): Promise<AgentGameStateSnapshot> =>
    waitForSnapshot(`waitForReflectionPhase(${phase})`, (s) => s.reflection?.phase === phase, timeoutMs)

  const carriedAmount = (item: AgentGameItem): number =>
    (lastState?.inventory ?? []).filter((slot) => slot.item === item).reduce((sum, slot) => sum + slot.amount, 0)

  const readRobot = async (robotId?: string): Promise<AgentGameRobotInfo | null> => {
    const result = await sendCommand('robotInfo', robotId ? { interactableId: robotId } : undefined)
    return result.status === 'done' ? (result.data as AgentGameRobotInfo) : null
  }

  const waitForRobotState = async (
    robotId: string,
    state: AgentGameRobotState,
    timeoutMs: number
  ): Promise<AgentGameRobotInfo | null> => {
    const deadline = Date.now() + timeoutMs
    let info = await readRobot(robotId)
    while (info?.state !== state && Date.now() < deadline) {
      await delay(ROBOT_POLL_MS)
      info = await readRobot(robotId)
    }
    return info
  }

  const startRobotUnlock = async (robotId: string): Promise<AgentGameCommandResult> => {
    const reached = await interactWithApproach(robotId)
    if (reached.status !== 'done') return reached

    const info = await waitForRobotState(robotId, AgentGameRobotState.AWAITING_CONFIRMATION, ROBOT_STATE_TIMEOUT_MS)
    if (info?.state !== AgentGameRobotState.AWAITING_CONFIRMATION || !info.displayedText) {
      return failed(`robot did not start its unlock (state ${info?.state ?? 'unknown'})`)
    }

    return { ...reached, data: { robotId, code: info.displayedText } }
  }

  const takeUridiumFromRobot = async (robotId: string): Promise<AgentGameCommandResult> => {
    const info = await waitForRobotState(robotId, AgentGameRobotState.OPEN, ROBOT_OPEN_TIMEOUT_MS)
    if (info?.state !== AgentGameRobotState.OPEN) return failed(`robot is not open (state ${info?.state ?? 'unknown'})`)

    const before = carriedAmount(AgentGameItem.URIDIUM)
    const taken = await interactWithApproach(robotId)
    if (taken.status !== 'done') return taken

    await delay(ITEM_SETTLE_MS)
    const after = carriedAmount(AgentGameItem.URIDIUM)
    return after > before
      ? { ...taken, data: { uridium: after } }
      : failed('no uridium came out (stunned, or the backpack is full)')
  }

  // The code buttons stay dark until the awaiting robot's marker is clicked on the map.
  const pressRobotCodeMatching = async (code: string): Promise<AgentGameCommandResult> => {
    const selected = await sendCommand('mapSelectRobot')
    if (selected.status !== 'done') return selected

    const deadline = Date.now() + ROBOT_STATE_TIMEOUT_MS
    let pressed = failed(`code '${code}' was not pressed`)
    while (Date.now() < deadline) {
      const info = await readRobot()
      const index = (info?.candidateCodes ?? []).findIndex((c) => c?.trim().toLowerCase() === code.trim().toLowerCase())
      if (index < 0) return failed(`code '${code}' not among candidates: ${(info?.candidateCodes ?? []).join(', ')}`)

      pressed = await sendCommand('robotCodePress', { index })
      if (pressed.status === 'done') return pressed
      await delay(ROBOT_POLL_MS)
    }
    return pressed
  }

  const goHome = async (): Promise<AgentGameCommandResult> => {
    if (carriedAmount(AgentGameItem.URIDIUM) > 0) {
      const deposited = await interactWithKnown(AgentGameKnownLocation.URIDIUM_DEPOSIT)
      if (deposited.status !== 'done') return deposited
    }
    return sendCommand('goToElevator')
  }

  const nearestRobot = async (path?: AgentGamePath): Promise<AgentGameLocation | undefined> => {
    const robots = await listLocations({ kind: AgentGameLocationKind.ROBOT, path })
    return nearest(robots.filter((robot) => !robot.state || robot.state === AgentGameRobotState.HARVESTING))
  }

  const api: AgentGameApi = {
    // Live: reflects whether the Unity bridge is actually reachable right now, not a
    // one-time snapshot from install (the iframe window can appear later).
    get ready() {
      return typeof getGameWindow()?.unityInstance?.SendMessage === 'function'
    },
    ping: () => sendCommand('ping'),
    listLocations,
    describeKnownLocations: () =>
      Object.fromEntries(Object.entries(AGENT_KNOWN_LOCATIONS).map(([key, m]) => [key, m.description])),
    goTo,
    collect,
    collectUridium: (path?: AgentGamePath) => collect(AgentGameItem.URIDIUM, path),
    collectOxygen: () => collect(AgentGameItem.OXYGEN),
    collectHeater: () => collect(AgentGameItem.HEATER),
    collectStimpack: () => collect(AgentGameItem.STIMPACK),
    depositUridium: () => interactWithKnown(AgentGameKnownLocation.URIDIUM_DEPOSIT),
    goToElevator: () => sendCommand('goToElevator'),
    leaveThroughElevator: () => sendCommand('leaveThroughElevator'),
    openNearestDoor: () => interactWithKnown(AgentGameKnownLocation.DOOR),
    moveTo: (x: number, z: number, y?: number) => sendCommand('moveTo', y === undefined ? { x, z } : { x, y, z }),
    interactWith: (locationIdOrName: string) => interactWithApproach(locationIdOrName),
    runDebugAction: (action: AgentGameDebugAction, target?: AgentGameDebugTarget) =>
      sendCommand('debug', { debugAction: action, target }),
    waitForPhase,
    waitForReflectionPhase,
    stop: () => sendCommand('stop'),
    reflectionReady: () => sendCommand('reflectionReady'),
    answerReflectionQuestion: (index: number) => sendCommand('reflectionAnswer', { index }),
    submitCircumplex: (x: number, y: number) => sendCommand('circumplexSubmit', { x, y }),
    reflectionNextLoop: () => sendCommand('reflectionNextLoop'),
    reflectionFinal: () => sendCommand('reflectionFinal'),
    reflectionQuit: () => sendCommand('reflectionQuit'),
    goToOnboardingExit: () => sendCommand('goToOnboardingExit'),
    getRobotInfo: readRobot,
    pressRobotCode: (index: number) => sendCommand('robotCodePress', { index }),
    pressRobotCodeMatching,
    selectRobotOnMap: (robotId?: string) =>
      sendCommand('mapSelectRobot', robotId ? { interactableId: robotId } : undefined),
    nearestRobot,
    startRobotUnlock,
    takeUridiumFromRobot,
    oxygenSecondsLeft: () => carriedAmount(AgentGameItem.OXYGEN),
    goHome,
    selectAbility: (ability: AgentGameAbility) => sendCommand('preplanSelectAbility', { ability }),
    togglePreplanReady: () => sendCommand('preplanToggleReady'),
    dropItem: (slot: number) => sendCommand('dropItem', { slot }),
    useStimpack: () => sendCommand('useStimpack'),
    useHeater: () => sendCommand('useHeater'),
    useAbility: () => sendCommand('useAbility'),
    callDrone: () => sendCommand('callDrone'),
    toggleCircumplexHud: () => sendCommand('toggleCircumplex'),
    abortGame: () => sendCommand('abortGame'),
    dronePuzzle: (interaction: AgentGameDronePuzzleInteraction) => sendCommand('dronePuzzle', { interaction }),
    openMap: () => sendCommand('openMap'),
    closeMap: () => sendCommand('closeMap'),
    placeFlag: (x: number, z: number) => sendCommand('placeFlag', { x, z }),
    startLobbyGame: () => sendCommand('lobbyStart'),
    leaveLobby: () => sendCommand('lobbyLeave'),
    confirmLobbyAudio: () => sendCommand('lobbyConfirmAudio'),
    resolveConfirmDialog: (confirm = true) => sendCommand('confirmDialog', { confirm }),
    sendCommand,
    getState: () => lastState,
    onState: (cb) => {
      stateListeners.add(cb)
      return () => stateListeners.delete(cb)
    }
  }

  const win = window as Window & { __agentGameApi?: AgentGameApi }
  win.__agentGameApi = api

  return () => {
    pending.forEach((entry) => clearTimeout(entry.timeout))
    pending.clear()
    stateListeners.clear()
    if (win.__agentGameApi === api) delete win.__agentGameApi
  }
}
